import { formatInstant, formatTemporalWithTime } from "@/utils/temporal";

import { ChatCitationSource } from "./types";

// The evidence footer's clauses (#11426, rule 7): every generated answer
// states how many distinct sources it cited and when it was generated.
//
// The as-of is an instant, so the day it names depends on who is reading: it
// renders in the zone the viewer resolved (their pin, else the browser's),
// never the host's locale. The full timestamp behind it (the tooltip) states
// the wall clock and the zone's name in that same zone, through the shared
// temporal wrapper, so the day and the clock can never disagree.

/** The instant an answer carries, as ISO 8601, or null when it carries none. */
const instantOf = (timestamp?: Date): string | null =>
  timestamp && !Number.isNaN(timestamp.getTime())
    ? timestamp.toISOString()
    : null;

/**
 * "May 27" — the viewer's calendar day, month and day only; null renders
 * nothing. The year and the words "as of" are dropped (Hunter, after
 * claude.ai's footer): the row is a timestamp under an answer, the tooltip
 * carries the full date, and a thread's footers read as a column of days.
 */
export const evidenceAsOf = (
  timestamp: Date | undefined,
  zone: string
): string | null => {
  const at = instantOf(timestamp);
  if (!at) return null;
  return formatInstant(at, zone, "monthDay");
};

/** "May 27, 2026 13:00 PDT" — the full instant in the viewer's zone. */
export const evidenceTimestamp = (
  timestamp: Date | undefined,
  zone: string
): string | null => {
  const at = instantOf(timestamp);
  if (!at) return null;
  return formatTemporalWithTime({ granularity: "instant", at, zone }, "medium");
};

// The footer's source clause. Zero sources states nothing (rule 3) — an
// answer that cited no documents must not claim "0 sources".
export const evidenceSources = (count: number): string | null =>
  count > 0 ? `${count} ${count === 1 ? "source" : "sources"}` : null;

/**
 * What a cited source is, which decides how it is labelled and what opens.
 *
 * The precedence mirrors the citation chip's: a structured-data citation that
 * names an RMS record is a record (the reader wants the investment, not the
 * query behind it, #11103); `rmsEntity` marks how an entry was harvested
 * while the publicId + kind pair is what the preview opens on.
 */
export type ChatSourceKind =
  | "document"
  | "record"
  | "interaction"
  | "data"
  | "artifact"
  // A calculation the answer recorded (#13618): opens its arithmetic.
  | "calculation";

export const chatSourceKind = (entry: ChatCitationSource): ChatSourceKind => {
  const opensRecord = Boolean(entry.publicId && entry.kind);
  if (entry.iodlSource === true && !opensRecord) return "data";
  if (entry.rmsEntity === true || opensRecord) return "record";
  if (entry.rmsInteraction === true) return "interaction";
  // An artifact (#11819): opened as itself, labelled by its title.
  if (entry.artifact === true && entry.artifactId) return "artifact";
  if (entry.calculation === true) return "calculation";
  return "document";
};

/** The name a source goes by — the same one its citation chip carries. */
export const chatSourceLabel = (entry: ChatCitationSource): string => {
  const sourceKind = chatSourceKind(entry);
  switch (sourceKind) {
    case "data":
      return `${entry.sourceLabel ?? "IODL"} data source`;
    case "record":
    case "interaction":
    case "artifact":
    case "calculation":
      return entry.name ?? "";
    case "document":
      return entry.parentDoc?.filename ?? "";
    default: {
      const unhandled: never = sourceKind;
      throw new Error(`unhandled source kind: ${String(unhandled)}`);
    }
  }
};

/**
 * What a structured-data citation opens: the query and the cited rows, the
 * shape the source viewer takes. The markdown plugin serialises it (arrays do
 * not survive as element props) and the chip parses it back once at its
 * boundary; the sources list hands it over as is.
 */
export interface ChatIodlSource {
  sql: string;
  columns: string[];
  rowCount: number;
  reasoning: string;
  rows: Array<Array<string | number | null>>;
  // Carried, or the viewer's own `?? "IODL"` fallback names the wrong data
  // set: the chip beside it reads the label off `entry` directly and got
  // this right, so a Transactions citation opened a panel titled "IODL data
  // source" while its own chip said "Transactions".
  sourceLabel: string | null;
}

export const chatSourceIodl = (entry: ChatCitationSource): ChatIodlSource => ({
  sql: entry.sql ?? "",
  columns: entry.columns ?? [],
  rowCount: entry.rowCount ?? entry.rows?.length ?? 0,
  reasoning: entry.reasoning ?? "",
  rows: entry.rows ?? [],
  sourceLabel: entry.sourceLabel ?? null,
});

/** One input of a recorded calculation, resolved against its source map. */
export interface ChatCalculationInput {
  value: string;
  marker: string;
  /** The input's own source; absent when the map never received it. */
  entry?: ChatCitationSource;
  /** Set when the input is itself a recorded calculation. */
  calculation?: ChatCalculationSource;
}

/**
 * What a calculation citation opens: its card. Resolved once against the
 * source map the citation came from, so the card can open each input where
 * that input's own chip would without holding the map itself.
 */
export interface ChatCalculationSource {
  name: string;
  result: string;
  expression: string;
  inputs: ChatCalculationInput[];
}

/**
 * An input's marker as the source map keys it: bracketed, `[N.M]` (or
 * `[[web:url]]`). A stored answer may carry the bare `N.M`; it is wrapped so
 * either shape resolves.
 */
export const calculationInputMarker = (marker: string): string =>
  marker.startsWith("[") ? marker : `[${marker}]`;

export const chatSourceCalculation = (
  entry: ChatCitationSource,
  map: Record<string, ChatCitationSource[]> = {},
  // The markers already being resolved, so an input that cites a calculation
  // above it cannot recurse forever.
  seen: ReadonlySet<string> = new Set()
): ChatCalculationSource => ({
  name: entry.name || "Calculation",
  result: entry.calculationResult ?? "",
  expression: entry.calculationExpression ?? "",
  inputs: (entry.calculationInputs ?? []).map(({ value, marker: given }) => {
    const marker = calculationInputMarker(given);
    const input: ChatCitationSource | undefined = map[marker]?.[0];
    const nested =
      input && chatSourceKind(input) === "calculation" && !seen.has(marker)
        ? chatSourceCalculation(input, map, new Set([...seen, marker]))
        : undefined;
    return { value, marker, entry: input, calculation: nested };
  }),
});

/**
 * The identity of a source: the underlying document, record, interaction or
 * data set, so repeated markers against one thing count once. A
 * structured-data citation's source is the data set it was read from — a turn
 * that runs several queries against Transactions consulted one source, and
 * the reader would not tell N rows all labelled "Transactions data source"
 * apart. Organization-owned sources expose no `sql` at all, which a query key
 * would have collapsed differently from the rest. Chunk text is the last
 * resort for an entry that names nothing. A recorded calculation (#13618) is
 * its own source wherever it is cited: its marker is its identity.
 */
export const chatSourceKey = (
  entry: ChatCitationSource,
  marker?: string
): string | undefined => {
  if (chatSourceKind(entry) === "calculation") return `calc:${marker ?? ""}`;
  const identity =
    entry.docId ??
    entry.parentDocId ??
    entry.publicId ??
    entry.interactionId ??
    entry.artifactId;
  if (identity) return identity;
  if (chatSourceKind(entry) === "data")
    return `data:${entry.sourceLabel ?? ""}`;
  return entry.content;
};

/** One distinct source an answer cited, with every page it was cited on. */
export interface EvidenceSource {
  key: string;
  /** Its citation number — the one its chips in the prose carry. */
  number: number;
  /** The first citation seen for this source; when that one carried no
   *  name, it wears the name of the first later citation that did. */
  entry: ChatCitationSource;
  /** Every bracketed marker that cites this source, first-cited order. */
  markers: string[];
  /** Cited pages, ascending, for document sources; empty otherwise. */
  pages: number[];
  /** A calculation source's card, resolved against its block's map. */
  calculation?: ChatCalculationSource;
}

/**
 * One block of generated prose and the citations it resolves against: a text
 * segment of an agentic turn, or a legacy message's whole body.
 */
export interface CitedBlock {
  text: string;
  map?: Record<string, ChatCitationSource[]>;
}

/**
 * The order the prose cites its markers in — where each first appears in the
 * text, then the map's own order for any the text never spells out. This is
 * the order the reader meets the chips in, so it is the order that numbers
 * them; the map's insertion order is whatever the backend emitted.
 */
const markersInProseOrder = (block: CitedBlock): string[] => {
  const keys = Object.keys(block.map ?? {});
  const position = new Map(
    keys.map((key) => {
      const at = block.text.indexOf(key);
      return [key, at === -1 ? Number.MAX_SAFE_INTEGER : at];
    })
  );
  return keys
    .map((key, index) => ({ key, index }))
    .sort(
      (a, b) =>
        (position.get(a.key) ?? 0) - (position.get(b.key) ?? 0) ||
        a.index - b.index
    )
    .map(({ key }) => key);
};

/**
 * The markers a block's map carries only as a calculation's inputs (#13618):
 * the prose never cites them, so they have no chip, and the calculation's
 * card is where they are read. An artifact's map comes with no prose to say
 * what it cites, so none of its markers is set aside.
 */
const inputOnlyMarkers = (block: CitedBlock): Set<string> => {
  const inputs = new Set<string>();
  if (!block.text) return inputs;
  for (const entries of Object.values(block.map ?? {})) {
    for (const entry of entries) {
      if (chatSourceKind(entry) !== "calculation") continue;
      for (const { marker } of entry.calculationInputs ?? []) {
        const key = calculationInputMarker(marker);
        if (!block.text.includes(key)) inputs.add(key);
      }
    }
  }
  return inputs;
};

/**
 * The distinct sources across one or more cited blocks, numbered in
 * first-cited order. One derivation numbers both the chips in the prose and
 * the rows of the sources list, so the list is the index of the chips: a
 * source cited at two markers carries one number in both places. An absent
 * map contributes nothing, and neither does an entry the map carries only as
 * a calculation's input.
 */
export const collectSources = (
  blocks: Array<CitedBlock | undefined>
): EvidenceSource[] => {
  const byKey = new Map<string, EvidenceSource>();
  for (const block of blocks) {
    if (!block?.map) continue;
    const inputsOnly = inputOnlyMarkers(block);
    for (const marker of markersInProseOrder(block)) {
      if (inputsOnly.has(marker)) continue;
      for (const entry of block.map[marker] ?? []) {
        const key = chatSourceKey(entry, marker);
        if (!key) continue;
        let source = byKey.get(key);
        if (!source) {
          source = {
            key,
            number: byKey.size + 1,
            entry,
            markers: [],
            pages: [],
            calculation:
              chatSourceKind(entry) === "calculation"
                ? chatSourceCalculation(entry, block.map, new Set([marker]))
                : undefined,
          };
          byKey.set(key, source);
        } else if (!chatSourceLabel(source.entry) && chatSourceLabel(entry)) {
          // A page cited from a full document read carries no filename; a
          // later citation of the same document that does name it labels
          // the source. Only the naming fields move: the number, the page
          // and the passage the first citation anchors stay its own.
          source.entry = {
            ...source.entry,
            parentDoc: entry.parentDoc,
            name: entry.name,
          };
        }
        if (!source.markers.includes(marker)) source.markers.push(marker);
        if (
          typeof entry.pageNum === "number" &&
          !source.pages.includes(entry.pageNum)
        ) {
          source.pages.push(entry.pageNum);
          source.pages.sort((a, b) => a - b);
        }
      }
    }
  }
  return [...byKey.values()];
};

/**
 * The number each marker's chip carries — read off the numbered sources, so
 * a chip and its row in the list can never disagree.
 */
export const citationNumbers = (
  sources: EvidenceSource[]
): Record<string, number> => {
  const numbers: Record<string, number> = {};
  for (const source of sources) {
    for (const marker of source.markers) {
      if (!(marker in numbers)) numbers[marker] = source.number;
    }
  }
  return numbers;
};
